// TODO: Figure out how to correctly add types from "applepayjs".
/// <reference types="applepayjs" />
import { Basket, Order } from '~/lib/data-contract/salesforce';
import { ClientSideSingleton as Session } from '~/services/altapay/session';

declare global {
    interface Window {
        ApplePaySession?: ApplePaySession;
    }
}

export enum ApplePayEvents {
    SUCCESS = 'ap-success',
    ERROR = 'ap-error',
    CANCEL = 'ap-cancel',
    PROCESSING = 'ap-processing',
}

export type ApplePaySuccessResponse = {
    status: string;
    resultUrl?: string;
    resultContent?: string;
    paymentId: string;
};

class ApplePay {
    private _authorizeUrl: string;
    private _sessionUrl: string;
    private _operatingCountry: string;
    private _token: string | undefined;
    private _sessionId: string | undefined;
    private _verifyOrder?: () => Promise<Order>;

    static readonly version: number = 14;

    session: ApplePaySession | undefined;

    constructor(
        private _paymentMethodId: string,
        private _orderNo: string,
        private _basket: Basket,
        _config: {
            authorizeUrl: string;
            sessionUrl: string;
            operatingCountry: string;
        },
    ) {
        this._token = Session.token;
        this._sessionId = Session.getSessionId();

        this._authorizeUrl = _config.authorizeUrl;
        this._sessionUrl = _config.sessionUrl;
        this._operatingCountry = _config.operatingCountry;

        this.createApplePayPaymentRequest = this.createApplePayPaymentRequest.bind(this);
    }

    private _extractBillingContactFromBasket(
        basket: Basket,
    ): ApplePayJS.ApplePayPaymentRequest['billingContact'] {
        const { billingAddress } = basket;

        const addressLines = [billingAddress?.address1, billingAddress?.address2].filter(
            (line: string | undefined): line is string => !!line,
        );

        return {
            givenName: billingAddress?.firstName,
            familyName: billingAddress?.lastName,
            addressLines,
            locality: billingAddress?.city,
            country: billingAddress?.countryCode,
            postalCode: billingAddress?.postalCode,
        };
    }

    private _extractShippingContactFromBasket(
        basket: Basket,
    ): ApplePayJS.ApplePayPaymentRequest['shippingContact'] {
        // TODO: Get shipping address from order or maybe basket?
        // TEMP: For testing purposes only.
        return this._extractBillingContactFromBasket(basket);
    }

    static async createIFrameWithCallbackContent(src: string) {
        const div = document.createElement('div');
        div.className = 'AltaPayEmbeddedApplePayResultsBackground';
        div.style.backgroundColor = 'white';
        div.style.position = 'fixed';
        div.style.top = '0px';
        div.style.left = '0px';
        div.style.width = '100%';
        div.style.height = '100%';

        const iframe = document.createElement('iframe');
        iframe.className = 'AltaPayEmbeddedApplePayResults';
        iframe.setAttribute('frameBorder', '0');
        iframe.setAttribute('scrolling', 'auto');
        iframe.setAttribute('style', 'position:fixed;top:0px;left:0px;width:100%;height:100%;');
        iframe.srcdoc = src;

        document.body.appendChild(div), div.appendChild(iframe);
    }

    private async _requestSession(event: ApplePayJS.ApplePayValidateMerchantEvent) {
        const payload = {
            validationUrl: event.validationURL,
            sessionId: this._sessionId,
            paymentMethodId: this._paymentMethodId,
        };

        try {
            const request = await fetch(this._sessionUrl, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + this._token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (request.ok) {
                const session = await request.json();

                this.session?.completeMerchantValidation(session);
            } else {
                throw new Error("ApplePay - Error: Couldn't validate session.");
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async _completePayment(event: ApplePayJS.ApplePayPaymentAuthorizedEvent) {
        const { payment } = event;

        const payload = {
            sessionId: this._sessionId,
            paymentMethodId: this._paymentMethodId,
            providerData: payment.token,
        };

        try {
            /**
             * If we have an order verification function, execute it before we finalize.
             * This should succeed. If this fails, it will be caught and payment will be completed with an error.
             */
            if (this._verifyOrder) await this._verifyOrder();

            const request = await fetch(this._authorizeUrl, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + this._token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (request.ok) {
                const data: ApplePaySuccessResponse = await request.json();

                if (data.status !== 'succeeded')
                    throw new Error(
                        'ApplePay - Error: Payment failed. Received status: ' + request.status,
                    );

                this.session?.completePayment({
                    status: ApplePaySession.STATUS_SUCCESS,
                });

                this.session?.dispatchEvent(
                    new CustomEvent<ApplePaySuccessResponse>(ApplePayEvents.SUCCESS, {
                        detail: data,
                    }),
                );
            } else {
                throw new Error(
                    "ApplePay - Error: Couldn't complete payment. Received status: " +
                        request.status,
                );
            }
        } catch (error) {
            this.session?.dispatchEvent(
                new CustomEvent<unknown>(ApplePayEvents.ERROR, { detail: error }),
            );

            this.session?.completePayment({
                errors: [
                    {
                        code: 'unknown',
                        message: String(error),
                    },
                ],
                status: ApplePaySession.STATUS_FAILURE,
            });
        }
    }

    private _updatePaymentMethodFees(_: ApplePayJS.ApplePayPaymentMethodSelectedEvent) {
        this.session?.completePaymentMethodSelection({
            newTotal: {
                label: this._orderNo ?? '',
                type: 'final',
                amount: (this._basket.c_remainingTotal ?? 0).toString(),
            },
            newLineItems: [],
        });
    }

    createApplePayPaymentRequest(
        operatingCountry: string,
        basket: Basket,
    ): ApplePayJS.ApplePayPaymentRequest {
        const lineItems: ApplePayJS.ApplePayLineItem[] =
            basket.productItems?.map((product) => ({
                label: product.productName ?? '',
                type: 'final',
                amount: (product.price ?? 0).toString(),
            })) ?? [];

        return {
            countryCode: operatingCountry,
            currencyCode: basket.currency ?? 'DKK',
            supportedNetworks: ['visa', 'mastercard'],
            merchantCapabilities: ['supports3DS'],
            total: {
                label: this._orderNo ?? '',
                type: 'final',
                amount: (basket.c_remainingTotal ?? 0).toString(),
            },
            lineItems,
            billingContact: this._extractBillingContactFromBasket(basket),
            shippingContact: this._extractShippingContactFromBasket(basket),
        };
    }

    setOrderVerification(fn: () => Promise<Order>) {
        this._verifyOrder = fn;
    }

    static async canRender(merchantId: string) {
        const canMakeApplePayPayments =
            typeof window.ApplePaySession !== 'undefined' &&
            (await ApplePaySession.canMakePaymentsWithActiveCard(merchantId)) &&
            ApplePaySession.supportsVersion(ApplePay.version);

        return canMakeApplePayPayments;
    }

    initiate() {
        const payload = this.createApplePayPaymentRequest(this._operatingCountry, this._basket);

        this.session = new ApplePaySession(ApplePay.version, payload);

        this.session.onvalidatemerchant = this._requestSession.bind(this);
        this.session.onpaymentmethodselected = this._updatePaymentMethodFees.bind(this);
        this.session.onpaymentauthorized = this._completePayment.bind(this);

        this.session.oncancel = () => this.session?.dispatchEvent(new Event(ApplePayEvents.CANCEL));

        this.session.begin();

        return this.session;
    }
}

export default ApplePay;
